import { memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Privilege } from "../../services/EmployeeService";
import "./PrivilegeDescription.css";
import BenefitCard from "../BenefitCard/BenefitCard";

const PrivilegeDescription = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const privilege = location.state?.privilege as Privilege | undefined;

  if (!privilege) {
    return (
      <div className="privilege-description-container">
        <p>Privilege not found</p>
        <button onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  const {
    name,
    description,
    image_url,
    address,
    phone,
    website_link,
    facebook_link,
    instagram_link,
    opening_hours,
    benefits,
  } = privilege;

  const imageUrl = image_url || "/placeholder.png";

  const getGoogleMapsUrl = (address: string) => {
    const encodedAddress = encodeURIComponent(address);
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  };

  const formatWebsiteUrl = (url: string) => {
    try {
      // Remove protocol (http:// or https://)
      let domain = url.replace(/^https?:\/\//, "");
      // Remove www. if present
      domain = domain.replace(/^www\./, "");
      // Remove everything after .com (or other TLD)
      const match = domain.match(/^([^/]+)/);
      return match ? match[1] : domain;
    } catch {
      return url;
    }
  };

  const formatPrivilegeNameWithHyphens = (text: string) => {
    // Add hyphens between words (split on spaces and join with hyphens)
    return text.split(/\s+/).join("-");
  };

  const getPhoneHref = (phoneNumber: string) => {
    const digits = phoneNumber.replace(/\D/g, "");
    return `tel:${digits || phoneNumber}`;
  };

  const formatOpeningHours = () => {
    if (!opening_hours) return null;
    const parts = [];
    if (opening_hours.weekdays?.open && opening_hours.weekdays?.close) {
      parts.push(
        `Sunday - Thursday: ${opening_hours.weekdays.open}-${opening_hours.weekdays.close}`,
      );
    }
    if (
      opening_hours.fridayAndErevYomTov?.open &&
      opening_hours.fridayAndErevYomTov?.close
    ) {
      parts.push(
        `Friday and Erev Yom Tov: ${opening_hours.fridayAndErevYomTov.open}-${opening_hours.fridayAndErevYomTov.close}`,
      );
    }
    return parts.length > 0 ? parts.join("\n") : null;
  };

  return (
    <div className="privilege-description-container">
      {privilege ? (
        <>
          <div
            className="privilege-description-image"
            style={{ backgroundImage: `url(${imageUrl})` }}
          >
            <button className="back-button-circle" onClick={() => navigate(-1)}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.83 19a1 1 0 0 1-.78-.37l-4.83-6a1 1 0 0 1 0-1.27l5-6a1 1 0 0 1 1.54 1.28L10.29 12l4.32 5.36a1 1 0 0 1-.78 1.64z"
                  fill="#222"
                />
              </svg>
            </button>
            <h1 className="privilege-description-title">{name}</h1>
          </div>
          <div className="privilege-description-centered-content">
            <div className="privilege-description-about-text">About {name}</div>
            {description && (
              <div className="privilege-description-about-description">
                {description}
              </div>
            )}
            <div className="privilege-description-available-text">
              Available privileges
            </div>
            {benefits && benefits.length > 0 && (
              <div className="privilege-description-benefits">
                {benefits.map((benefit) => (
                  <BenefitCard key={benefit.id} benefit={benefit} />
                ))}
              </div>
            )}
            <div className="privilege-description-information-text">
              Information
            </div>
            <div className="privilege-description-address">
              {address && (
                <div className="privilege-description-address-block">
                  <div className="privilege-description-address-row">
                    <img
                      src="/map.svg"
                      alt="Map"
                      className="privilege-description-map-icon"
                    />
                    <span className="privilege-description-address-text">
                      {address}
                    </span>
                  </div>
                  <a
                    href={getGoogleMapsUrl(address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="privilege-description-open-maps-link"
                  >
                    Open in maps
                  </a>
                </div>
              )}
              {formatOpeningHours() && (
                <div className="privilege-description-opening-hours">
                  <img
                    src="/time.svg"
                    alt="Time"
                    className="privilege-description-time-icon"
                  />
                  <span className="privilege-description-opening-hours-text">
                    {formatOpeningHours()}
                  </span>
                </div>
              )}
              {phone && (
                <a
                  href={getPhoneHref(phone)}
                  className="privilege-description-phone"
                >
                  <img
                    src="/phone.svg"
                    alt="Phone"
                    className="privilege-description-phone-icon"
                  />
                  <span className="privilege-description-phone-text">
                    {phone}
                  </span>
                </a>
              )}

              {(website_link || facebook_link || instagram_link) && (
                <div className="privilege-description-links">
                  {website_link && (
                    <a
                      href={website_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="privilege-description-website-link"
                    >
                      <img
                        src="/glb.svg"
                        alt="Website"
                        className="privilege-description-website-icon"
                      />
                      <span>{formatWebsiteUrl(website_link)}</span>
                    </a>
                  )}
                  {facebook_link && (
                    <a
                      href={facebook_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="privilege-description-website-link"
                    >
                      <img
                        src="/facebook.svg"
                        alt="Facebook"
                        className="privilege-description-website-icon"
                      />
                      <span>{formatPrivilegeNameWithHyphens(name)}</span>
                    </a>
                  )}
                  {instagram_link && (
                    <a
                      href={instagram_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="privilege-description-website-link"
                    >
                      <img
                        src="/instagram.svg"
                        alt="Instagram"
                        className="privilege-description-website-icon"
                      />
                      <span>{formatPrivilegeNameWithHyphens(name)}</span>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="privilege-description-container">
          <p>Privilege not found</p>
          <button onClick={() => navigate(-1)}>Go Back</button>
        </div>
      )}
    </div>
  );
};

export default memo(PrivilegeDescription);
